import type { DraftJobViewModel } from '@/features/jobs/drafting/DraftJobViewModel'
import { observer } from 'mobx-react-lite'
import { DraftJobWizardContentHeaderContainer } from '@/features/jobs/drafting/components/DraftJobView/DraftJobWizardContentHeaderContainer'
import { MinidownContent } from '@/components/MinidownContent'
import { AnswerEditorRendererContainer } from '@/features/jobs/questions/components/AnswerEditorRendererContainer'
import { AnswerEditorBody } from '@/features/jobs/questions/components/AnswerEditorBody'
import { Prose } from '@/components/Prose'
import { WizardContentBody } from '@/components/Wizard'
import { useFormField } from '@/components/Form'
import { AnswerEditorBodyErrorHint } from '@/features/jobs/questions/components/AnswerEditorBodyErrorHint'

interface QuestionnaireStepViewContainerProps {
  vm: DraftJobViewModel
}

/**
 * Questionnaire Step View Container
 */
export const QuestionnaireStepViewContainer = observer(
  function QuestionnaireStepViewContainer({
    vm,
  }: QuestionnaireStepViewContainerProps) {
    const currentQuestionStepVm = vm.questionnaireVm.currentQuestionStepVm
    const isInvalid = currentQuestionStepVm?.validationErrors.length !== 0
    const { control, hint, label } = useFormField({
      name: `question_${currentQuestionStepVm?.questionVm.id.toLocaleLowerCase()}`,
      invalid: isInvalid,
    })

    if (!currentQuestionStepVm) {
      return null
    }

    const { questionVm, isDisabled } = currentQuestionStepVm

    return (
      <>
        <DraftJobWizardContentHeaderContainer
          vm={vm}
          isSkeleton={vm.prepare.pending}
          title={questionVm.title}
          titleId={label.id}
          titleTestId={`question-title-${questionVm.id}`}
          descriptionTestId={`question-description-${questionVm.id}`}
          description={
            questionVm.description && (
              <Prose>
                <MinidownContent value={questionVm.description} />
              </Prose>
            )
          }
        />
        <WizardContentBody>
          <AnswerEditorBody>
            <AnswerEditorRendererContainer
              questionId={questionVm.id}
              editorVm={questionVm.editor}
              isDisabled={isDisabled}
              isInvalid={isInvalid}
              {...control}
            />
            <AnswerEditorBodyErrorHint
              {...hint}
              errors={currentQuestionStepVm.validationErrors}
            />
          </AnswerEditorBody>
        </WizardContentBody>
      </>
    )
  },
)
